import React, { useState, useEffect, useMemo } from 'react';
import { 
  BarChart, 
  Calendar, 
  Filter, 
  Search, 
  TrendingUp, 
  DollarSign, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  X
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import { useAppStore } from '../store';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../utils/formatters';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Define filter types
interface Filters {
  clientId: string;
  productionId: string;
  taskType: string;
  supplierId: string;
  taskStatus: string;
  dateRange: string;
  startDate: string;
  endDate: string;
  period: string;
  customDateRange: boolean;
}

// Define KPI types
interface KPIs {
  averageProfitability: number;
  totalCost: number;
  totalValue: number;
  pendingTasksCount: number;
  inProgressTasksCount: number;
  finishedTasksCount: number;
}

const ProductionAdmin: React.FC = () => {
  const { productions, clients, suppliers, productionTasks } = useAppStore();
  // Cambiar el valor inicial de filters para que el período por defecto sea el mes corriente
  const [filters, setFilters] = useState<Filters>({
    clientId: 'all',
    productionId: 'all',
    taskType: 'all',
    supplierId: 'all',
    taskStatus: 'all',
    dateRange: 'created_at',
    startDate: '',
    endDate: '',
    period: 'month', // por defecto mes corriente
    customDateRange: false,
  });
  const [taskTypes, setTaskTypes] = useState<{id: string, name: string}[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [kpis, setKpis] = useState<KPIs>({
    averageProfitability: 0,
    totalCost: 0,
    totalValue: 0,
    pendingTasksCount: 0,
    inProgressTasksCount: 0,
    finishedTasksCount: 0,
  });
  const [taskTypeChartData, setTaskTypeChartData] = useState<any>(null);
  const [supplierChartData, setSupplierChartData] = useState<any>(null);
  const isDarkMode = document.documentElement.classList.contains('dark');

  // Get productions for the selected client
  const clientProductions = useMemo(() => {
    if (filters.clientId === 'all') return [];
    return productions.filter(p => p.client_id === filters.clientId);
  }, [productions, filters.clientId]);

  // Fetch task types
  useEffect(() => {
    const fetchTaskTypes = async () => {
      try {
        const { data, error } = await supabase
          .from('task_types')
          .select('id, name')
          .order('name');
        
        if (error) throw error;
        setTaskTypes(data || []);
      } catch (error) {
        console.error('Error fetching task types:', error);
      }
    };

    fetchTaskTypes();
  }, []);

  // Apply date filters based on period or custom date range
  useEffect(() => {
    if (filters.customDateRange) {
      // If custom date range is selected, don't modify the dates
      return;
    }
    
    if (filters.period === 'all') {
      setFilters(prev => ({ ...prev, startDate: '', endDate: '' }));
      return;
    }

    const now = new Date();
    let startDate = new Date();
    
    switch (filters.period) {
      case '7days':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30days':
        startDate.setDate(now.getDate() - 30);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        startDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
    }

    setFilters(prev => ({
      ...prev,
      startDate: startDate.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
    }));
  }, [filters.period, filters.customDateRange]);

  // Reset productionId when clientId changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      productionId: 'all'
    }));
  }, [filters.clientId]);

  // Filter productions based on criteria
  const filteredProductions = useMemo(() => {
    return productions.filter(production => {
      // Filter by client
      if (filters.clientId !== 'all' && production.client_id !== filters.clientId) {
        return false;
      }

      // Filter by production ID
      if (filters.productionId !== 'all' && production.id !== filters.productionId) {
        return false;
      }

      // Filter by date range
      if (filters.startDate && filters.endDate) {
        const productionDate = new Date(
          filters.dateRange === 'created_at' 
            ? production.created_at 
            : production.delivery_date
        );
        const startDate = new Date(filters.startDate);
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59); // Include the entire end day

        if (productionDate < startDate || productionDate > endDate) {
          return false;
        }
      }

      return true;
    });
  }, [productions, filters]);

  // Get production IDs for task filtering
  const filteredProductionIds = useMemo(() => {
    return filteredProductions.map(p => p.id);
  }, [filteredProductions]);

  // Filter tasks based on criteria
  const filteredTasks = useMemo(() => {
    return productionTasks.filter(task => {
      // Filter by production
      if (!filteredProductionIds.includes(task.production_id)) {
        return false;
      }

      // Filter by task type
      if (filters.taskType !== 'all' && task.task_type_id !== filters.taskType) {
        return false;
      }

      // Filter by supplier
      if (filters.supplierId !== 'all' && task.supplier_id !== filters.supplierId) {
        return false;
      }

      // Filter by task status
      if (filters.taskStatus !== 'all' && task.status !== filters.taskStatus) {
        return false;
      }

      return true;
    });
  }, [productionTasks, filteredProductionIds, filters]);

  // Calculate KPIs
  useEffect(() => {
    // Calculate total cost from filtered tasks
    const totalCost = filteredTasks.reduce((sum, task) => sum + task.cost, 0);
    
    // Calculate total production value from filtered productions
    const totalValue = filteredProductions.reduce((sum, prod) => sum + prod.production_value, 0);
    
    // Calculate average profitability
    const averageProfitability = totalValue > 0 
      ? ((totalValue - totalCost) / totalValue) * 100 
      : 0;
    
    // Count tasks by status
    const pendingTasksCount = filteredTasks.filter(task => task.status === 'pending').length;
    const inProgressTasksCount = filteredTasks.filter(task => task.status === 'in_progress').length;
    const finishedTasksCount = filteredTasks.filter(task => task.status === 'finished').length;
    
    setKpis({
      averageProfitability,
      totalCost,
      totalValue,
      pendingTasksCount,
      inProgressTasksCount,
      finishedTasksCount,
    });
  }, [filteredTasks, filteredProductions]);

  // Prepare chart data for task types
  useEffect(() => {
    // Group tasks by type and calculate total cost
    const taskTypeData: Record<string, number> = {};
    
    filteredTasks.forEach(task => {
      if (task.task_type_id) {
        if (!taskTypeData[task.task_type_id]) {
          taskTypeData[task.task_type_id] = 0;
        }
        taskTypeData[task.task_type_id] += task.cost;
      }
    });
    
    // Get task type names
    const labels = Object.keys(taskTypeData).map(typeId => {
      const taskType = taskTypes.find(t => t.id === typeId);
      return taskType?.name || 'Desconocido';
    });
    
    // Define colors for each bar
    const barColors = [
      'rgba(75, 192, 192, 0.6)', // Teal
      'rgba(153, 102, 255, 0.6)', // Purple
      'rgba(255, 159, 64, 0.6)', // Orange
      'rgba(255, 99, 132, 0.6)', // Pink
      'rgba(54, 162, 235, 0.6)', // Blue
      'rgba(255, 206, 86, 0.6)', // Yellow
      'rgba(231, 233, 237, 0.6)', // Grey
      'rgba(75, 192, 192, 0.6)', // Teal (repeat)
      'rgba(153, 102, 255, 0.6)', // Purple (repeat)
      'rgba(255, 159, 64, 0.6)', // Orange (repeat)
    ];
    
    // Prepare chart data
    setTaskTypeChartData({
      labels,
      datasets: [
        {
          label: 'Costo por Tipo de Tarea',
          data: Object.values(taskTypeData),
          backgroundColor: Object.keys(taskTypeData).map((_, i) => barColors[i % barColors.length]),
          borderColor: Object.keys(taskTypeData).map((_, i) => barColors[i % barColors.length].replace('0.6', '1')),
          borderWidth: 1,
        },
      ],
    });
  }, [filteredTasks, taskTypes, isDarkMode]);

  // Prepare chart data for suppliers
  useEffect(() => {
    // Group tasks by supplier and calculate total cost
    const supplierData: Record<string, number> = {};
    
    filteredTasks.forEach(task => {
      if (task.supplier_id) {
        if (!supplierData[task.supplier_id]) {
          supplierData[task.supplier_id] = 0;
        }
        supplierData[task.supplier_id] += task.cost;
      }
    });
    
    // Get supplier names
    const labels = Object.keys(supplierData).map(supplierId => {
      const supplier = suppliers.find(s => s.id === supplierId);
      return supplier?.name || 'Desconocido';
    });
    
    // Define vibrant colors for the pie chart
    const backgroundColors = [
      '#FF6384', // Pink
      '#36A2EB', // Blue
      '#FFCE56', // Yellow
      '#4BC0C0', // Teal
      '#9966FF', // Purple
      '#FF9F40', // Orange
      '#8AC926', // Green
      '#F94144', // Red
      '#43AA8B', // Seafoam
      '#277DA1', // Blue-green
      '#F8961E', // Amber
      '#577590', // Steel blue
      '#90BE6D', // Olive
      '#F9C74F', // Gold
      '#F3722C', // Burnt orange
      '#C9184A', // Crimson
      '#4D908E', // Teal green
      '#F15BB5', // Hot pink
      '#9B5DE5', // Lavender
      '#00BBF9', // Sky blue
    ];
    
    // If we need more colors than we have defined, generate them
    if (labels.length > backgroundColors.length) {
      for (let i = backgroundColors.length; i < labels.length; i++) {
        const hue = (i * 137.5) % 360; // Golden ratio to spread colors evenly
        backgroundColors.push(`hsl(${hue}, 70%, 60%)`);
      }
    }
    
    // Create border colors (slightly darker versions of background colors)
    const borderColors = backgroundColors.map(color => {
      if (color.startsWith('#')) {
        // For hex colors, just return the same color
        return color;
      } else if (color.startsWith('hsl')) {
        // For HSL colors, reduce lightness for border
        const match = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
        if (match) {
          const [_, h, s, l] = match;
          return `hsl(${h}, ${s}%, ${Math.max(parseInt(l) - 10, 0)}%)`;
        }
      }
      return color;
    });
    
    // Prepare chart data for pie chart
    setSupplierChartData({
      labels,
      datasets: [
        {
          label: 'Costo por Proveedor',
          data: Object.values(supplierData),
          backgroundColor: backgroundColors.slice(0, labels.length),
          borderColor: borderColors.slice(0, labels.length),
          borderWidth: 1,
        },
      ],
    });
  }, [filteredTasks, suppliers, isDarkMode]);

  // Bar chart options
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: isDarkMode ? '#e0e0e0' : '#333',
        },
      },
      tooltip: {
        backgroundColor: isDarkMode ? '#374151' : '#fff',
        titleColor: isDarkMode ? '#e0e0e0' : '#333',
        bodyColor: isDarkMode ? '#e0e0e0' : '#333',
        borderColor: isDarkMode ? '#4b5563' : '#e2e8f0',
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: number) => formatCurrency(value),
          color: isDarkMode ? '#e0e0e0' : '#333',
        },
        grid: {
          display: false
        }
      },
      x: {
        ticks: {
          color: isDarkMode ? '#e0e0e0' : '#333',
        },
        grid: {
          display: false
        }
      }
    },
  };

  // Pie chart options
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        display: true,
        labels: {
          color: isDarkMode ? '#e0e0e0' : '#333',
          font: {
            size: 12
          },
          padding: 20,
          boxWidth: 15,
          boxHeight: 15,
          generateLabels: function(chart: any) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label: string, i: number) => {
                const dataset = data.datasets[0];
                const value = dataset.data[i];
                const total = dataset.data.reduce((acc: number, val: number) => acc + val, 0);
                const percentage = Math.round((value / total) * 100);
                
                return {
                  text: `${label} - ${formatCurrency(value)} (${percentage}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.borderColor[i],
                  lineWidth: 1,
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        },
      },
      tooltip: {
        backgroundColor: isDarkMode ? '#374151' : '#fff',
        titleColor: isDarkMode ? '#e0e0e0' : '#333',
        bodyColor: isDarkMode ? '#e0e0e0' : '#333',
        borderColor: isDarkMode ? '#4b5563' : '#e2e8f0',
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = formatCurrency(context.raw);
            const percentage = Math.round(context.parsed * 100 / context.dataset.data.reduce((a: number, b: number) => a + b, 0)) + '%';
            return `${label}: ${value} (${percentage})`;
          }
        }
      }
    },
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      clientId: 'all',
      productionId: 'all',
      taskType: 'all',
      supplierId: 'all',
      taskStatus: 'all',
      dateRange: 'created_at',
      startDate: '',
      endDate: '',
      period: 'all',
      customDateRange: false,
    });
  };

  // Toggle custom date range
  const handleToggleCustomDateRange = (value: boolean) => {
    setFilters(prev => ({
      ...prev,
      customDateRange: value,
      period: value ? 'custom' : prev.period
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Producción</h2>
        <Button
          variant="outline"
          icon={<Filter className="h-5 w-5" />}
          onClick={handleResetFilters}
        >
          Limpiar Filtros
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader title="Filtros" />
        <CardContent>
          {/* Filtros compactos */}
          <div className="flex flex-wrap md:flex-nowrap items-end gap-2 md:gap-3 lg:gap-4">
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Cliente"
                value={filters.clientId}
                onChange={(value) => setFilters(prev => ({ ...prev, clientId: value }))}
                options={[
                  { value: 'all', label: 'Todos' },
                  ...clients.map(client => ({ value: client.id, label: client.name })),
                ]}
              />
            </div>
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Producción"
                value={filters.productionId}
                onChange={(value) => setFilters(prev => ({ ...prev, productionId: value }))}
                options={[
                  { value: 'all', label: 'Todas' },
                  ...clientProductions.map(production => ({ value: production.id, label: production.name })),
                ]}
              />
            </div>
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Tipo"
                value={filters.taskType}
                onChange={(value) => setFilters(prev => ({ ...prev, taskType: value }))}
                options={[
                  { value: 'all', label: 'Todos' },
                  ...taskTypes.map(type => ({ value: type.id, label: type.name })),
                ]}
              />
            </div>
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Proveedor"
                value={filters.supplierId}
                onChange={(value) => setFilters(prev => ({ ...prev, supplierId: value }))}
                options={[
                  { value: 'all', label: 'Todos' },
                  ...suppliers.map(supplier => ({ value: supplier.id, label: supplier.name })),
                ]}
              />
            </div>
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Estado"
                value={filters.taskStatus}
                onChange={(value) => setFilters(prev => ({ ...prev, taskStatus: value }))}
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'pending', label: 'Pendiente' },
                  { value: 'in_progress', label: 'En Proceso' },
                  { value: 'finished', label: 'Terminado' },
                ]}
              />
            </div>
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Período"
                value={filters.period}
                onChange={(value) => {
                  const isCustom = value === 'custom';
                  setFilters(prev => ({ 
                    ...prev, 
                    period: value,
                    customDateRange: isCustom
                  }));
                }}
                options={[
                  { value: 'all', label: 'Todo' },
                  { value: '7days', label: '7 días' },
                  { value: '30days', label: '30 días' },
                  { value: 'month', label: 'Este mes' },
                  { value: 'quarter', label: 'Trimestre' },
                  { value: 'year', label: 'Este año' },
                  { value: 'custom', label: 'Personalizado' },
                ]}
              />
            </div>
          </div>
          {/* Custom date range inputs */}
          {filters.customDateRange && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Input
                label="Fecha desde"
                type="date"
                id="startDate"
                name="startDate"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
              <Input
                label="Fecha hasta"
                type="date"
                id="endDate"
                name="endDate"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPIs - Financial */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-full mr-3">
                <TrendingUp className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Rentabilidad</p>
                <p className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">
                  {kpis.averageProfitability.toFixed(2)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full mr-3">
                <DollarSign className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Costo Total</p>
                <p className="text-xl font-semibold text-red-600 dark:text-red-400">
                  {formatCurrency(kpis.totalCost)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full mr-3">
                <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Valor Total</p>
                <p className="text-xl font-semibold text-green-600 dark:text-green-400">
                  {formatCurrency(kpis.totalValue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPIs - Task Status */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Tareas Pendientes</p>
                <div className="flex items-center mt-1">
                  <span className="text-3xl font-bold text-amber-800 dark:text-amber-200">{kpis.pendingTasksCount}</span>
                </div>
              </div>
              <div className="p-3 bg-amber-200 dark:bg-amber-800 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-800 dark:text-amber-200" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Tareas En Proceso</p>
                <div className="flex items-center mt-1">
                  <span className="text-3xl font-bold text-blue-800 dark:text-blue-200">{kpis.inProgressTasksCount}</span>
                </div>
              </div>
              <div className="p-3 bg-blue-200 dark:bg-blue-800 rounded-full">
                <Clock className="h-6 w-6 text-blue-800 dark:text-blue-200" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">Tareas Terminadas</p>
                <div className="flex items-center mt-1">
                  <span className="text-3xl font-bold text-green-800 dark:text-green-200">{kpis.finishedTasksCount}</span>
                </div>
              </div>
              <div className="p-3 bg-green-200 dark:bg-green-800 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-800 dark:text-green-200" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Costo por Tipo de Tarea" />
          <CardContent>
            <div className="h-80">
              {taskTypeChartData && taskTypeChartData.labels.length > 0 ? (
                <Bar data={taskTypeChartData} options={barChartOptions} />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  No hay datos disponibles para mostrar
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Costo por Proveedor" />
          <CardContent>
            <div className="h-80">
              {supplierChartData && supplierChartData.labels.length > 0 ? (
                <Pie data={supplierChartData} options={pieChartOptions} />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  No hay datos disponibles para mostrar
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary */}
      <Card>
        <CardHeader title="Resumen de Producciones" />
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Producción
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Costo
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Rentabilidad
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tareas
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredProductions.map((production) => {
                  const productionTasks = filteredTasks.filter(task => task.production_id === production.id);
                  const totalCost = productionTasks.reduce((sum, task) => sum + task.cost, 0);
                  const profitability = production.production_value > 0 
                    ? ((production.production_value - totalCost) / production.production_value) * 100 
                    : 0;
                  
                  const client = clients.find(c => c.id === production.client_id);
                  
                  return (
                    <tr key={production.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {production.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {client?.name || 'Cliente no encontrado'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                        {formatCurrency(production.production_value)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                        {formatCurrency(totalCost)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <span className={`
                          ${profitability > 30 ? 'text-green-600 dark:text-green-400' : 
                            profitability > 15 ? 'text-blue-600 dark:text-blue-400' : 
                            'text-red-600 dark:text-red-400'}
                        `}>
                          {profitability.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <div className="flex justify-center space-x-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                            {productionTasks.filter(t => t.status === 'pending').length}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                            {productionTasks.filter(t => t.status === 'in_progress').length}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
                            {productionTasks.filter(t => t.status === 'finished').length}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredProductions.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No hay producciones que coincidan con los filtros
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductionAdmin;